import React from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGraduationCap,
  faBookOpen,
  faArrowLeft,
  faCheckCircle,
  faUsers,
  faBlog,
  faLightbulb,
  faBook,
} from "@fortawesome/free-solid-svg-icons";

const services = [
  {
    id: 1,
    num: "01",
    title: "اختبارات إلكترونية",
    description:
      "نظام تقييم ذكي ومتقدم يساعد الطلاب على تحديد مستواهم بدقة عبر اختبارات تفاعلية.",
    icon: faGraduationCap,
    features: ["نتائج فورية وتحليل دقيق", "بنك أسئلة شامل ومتنوع", "تقارير أداء مفصلة"],
    accent: "#6366f1",
    glow: "rgba(232,146,10,0.12)",
    tabKey: "exams",
  },
  {
    id: 2,
    num: "02",
    title: "دورات تعليمية",
    description:
      "مكتبة ضخمة من الدورات التدريبية في مختلف المجالات لتعزيز مهاراتك ومعرفتك.",
    icon: faBookOpen,
    features: ["محتوى تفاعلي عالي الجودة", "نخبة من أفضل المعلمين", "شهادات إتمام معتمدة"],
    accent: "#0891b2",
    glow: "rgba(8,145,178,0.1)",
    tabKey: "courses",
  },
  {
    id: 3,
    num: "03",
    title: "مجتمع معرفي",
    description:
      "بيئة تفاعلية تشبه شبكات التواصل، تتيح تبادل الخبرات ومشاركة المقالات التعليمية.",
    icon: faBlog,
    features: ["نقاشات وتفاعل بناء", "نشر محتوى تعليمي هادف", "نظام متابعة وإعجابات"],
    accent: "#9333ea",
    glow: "rgba(147,51,234,0.1)",
    tabKey: "blogs",
  },
  {
    id: 4,
    num: "04",
    title: "مذكرات وملخصات",
    description:
      "مكتبة رقمية متكاملة تتيح للطلاب تحميل وتصفح المذكرات والملخصات الدراسية الموثوقة بسهولة.",
    icon: faBook,
    features: ["ملفات PDF منظمة ومنسقة", "بحث سريع للمواد", "توليد عروض تقديمية بـ AI"],
    accent: "#10b981",
    glow: "rgba(16,185,129,0.12)",
    tabKey: "mozakara",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Services = () => {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>الخدمات - منصة امتحاني</title>
        <meta name="description" content="خدمات تعليمية متكاملة: اختبارات، دورات، ومجتمع معرفي." />
      </Helmet>

      <section className="sv-root" id="services">


        <div className="sv-container">

          {/* ── header ── */}
          <motion.div
            className="sv-header"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            <motion.div className="sv-eyebrow" variants={fadeUp} custom={0}>
              <span className="sv-eyebrow-dot" />
              خدماتنا
            </motion.div>
            <motion.h2 className="sv-title" variants={fadeUp} custom={1}>
              تجربة تعليمية
              <em>بمستوى جديد</em>
            </motion.h2>
            <motion.p className="sv-subtitle" variants={fadeUp} custom={2}>
              حلول مصممة لتجعل رحلتك التعليمية أسرع، أوضح، وأكثر تفاعلاً
              <br className="sv-br" />من أول خطوة حتى النجاح.
            </motion.p>
          </motion.div>

          {/* ── cards ── */}
          <motion.div
            className="sv-cards"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
          >
            {services.map((s, i) => (
              <motion.article
                key={s.id}
                className="sv-card"
                variants={fadeUp}
                custom={i}
                whileHover="hover"
                initial="rest"
                animate="rest"
              >
                {/* glow bloom on hover */}
                <motion.div
                  className="sv-card-glow"
                  style={{ background: s.glow }}
                  variants={{ rest: { opacity: 0, scale: 0.7 }, hover: { opacity: 1, scale: 1 } }}
                  transition={{ duration: 0.35 }}
                />

                <div className="sv-card-body">
                  {/* top row */}
                  <div className="sv-card-top">
                    <span className="sv-num" style={{ color: s.accent }}>
                      {s.num}
                    </span>
                    <div
                      className="sv-icon-wrap"
                      style={{
                        background: `${s.accent}18`,
                        border: `1.5px solid ${s.accent}40`,
                      }}
                    >
                      <FontAwesomeIcon
                        icon={s.icon}
                        style={{ color: s.accent, fontSize: "1.4rem" }}
                      />
                    </div>
                  </div>

                  <h3 className="sv-card-title">{s.title}</h3>
                  <p className="sv-card-desc">{s.description}</p>

                  <ul className="sv-features">
                    {s.features.map((f, idx) => (
                      <li key={idx} className="sv-feature-item">
                        <FontAwesomeIcon
                          icon={faCheckCircle}
                          className="sv-check"
                          style={{ color: s.accent }}
                        />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <motion.button
                    className="sv-card-btn"
                    style={{
                      color: s.accent,
                      borderColor: `${s.accent}50`,
                    }}
                    onClick={() => navigate(`/services?tab=${s.tabKey}`)}
                    whileHover={{ x: -4 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <span>استكشف الخدمة</span>
                    <FontAwesomeIcon icon={faArrowLeft} />
                  </motion.button>
                </div>

                {/* bottom accent bar */}
                <div className="sv-card-bar" style={{ background: s.accent }} />
              </motion.article>
            ))}
          </motion.div>

          {/* ── CTA strip ── */}
          <motion.div
            className="sv-cta"
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="sv-cta-shimmer" />
            <div className="sv-cta-left">
              <div className="sv-cta-icon">
                <FontAwesomeIcon icon={faLightbulb} />
              </div>
              <div>
                <h3 className="sv-cta-title">جاهز للانطلاقة التالية؟</h3>
                <p className="sv-cta-desc">
                  ابدأ الآن مع خدمات امتحاني واكتشف كيف تتحول المذاكرة إلى نتائج حقيقية.
                </p>
              </div>
            </div>
            <button className="sv-cta-btn" onClick={() => navigate("/services")}>
              <FontAwesomeIcon icon={faUsers} style={{ color: "#fff" }} />
              <span style={{ color: "#fff" }}>استعرض كل الخدمات</span>
            </button>
          </motion.div>

        </div>
      </section>

      <style>{`
        /* ── Root ── */
        .sv-root {
          --gold: #6366f1;
          --teal: #0891b2;
          --lavender: #9333ea;
          --bg: #fff;
          --surface: #fff;
          --surface-2: #fff;
          --border: rgba(15,23,42,0.08);
          --border-hover: rgba(15,23,42,0.15);
          --text: #0f172a;
          --muted: #fff;
            font-family: "Zain", sans-serif;
          direction: rtl;
          position: relative;
          padding: 120px 0 100px;
          background: var(--bg);
          overflow: hidden;
          isolation: isolate;
        }



        /* ── Container ── */
        .sv-container {
          position: relative; z-index: 2;
          max-width: 1360px; margin: 0 auto; padding: 0 32px;
        }
        @media(min-width:1200px){ .sv-container { padding: 0 56px; } }

        /* ── Header ── */
        .sv-header { text-align: center; margin-bottom: 72px; }
        .sv-eyebrow {
          display: inline-flex; align-items: center; gap: 10px;
          color: var(--gold); font-weight: 800; font-size: 0.85rem;
          letter-spacing: 0.14em; text-transform: uppercase;
          margin-bottom: 24px;
        }
        .sv-eyebrow-dot {
          width: 8px; height: 8px; border-radius: 50%; background: var(--gold);
          animation: sv-pulse 2.2s ease-in-out infinite;
        }
        @keyframes sv-pulse {
          0%,100%{ box-shadow: 0 0 0 0 rgba(232,146,10,0.5); }
          50%{ box-shadow: 0 0 0 8px rgba(232,146,10,0); }
        }
        .sv-title {
          font-size: clamp(2.4rem, 4.5vw, 4rem);
          font-weight: 900; color: var(--text); margin: 0 0 20px;
          line-height: 1.18; letter-spacing: -0.025em;
        }
        .sv-title em {
          font-style: normal;
          background: linear-gradient(90deg, var(--gold) 0%, #6366f1 60%, var(--gold) 100%);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .sv-subtitle {
          font-size: 1.1rem; color: var(--muted); line-height: 1.85; margin: 0;
        }
        .sv-br { display: none; }
        @media(min-width:640px){ .sv-br { display: block; } }

        /* ── Cards grid ── */
        .sv-cards {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          margin-bottom: 48px;
        }
        @media(max-width:1200px){ .sv-cards { grid-template-columns: repeat(2,1fr); } }
        @media(max-width:680px){ .sv-cards { grid-template-columns: 1fr; } }

        /* ── Card ── */
        .sv-card {
          position: relative;
          border-radius: 24px;
          border: 1px solid var(--border);
          background: var(--surface);
          overflow: hidden;
          cursor: pointer;
          transition: border-color 0.3s;
        }
        .sv-card:hover { border-color: var(--border-hover); box-shadow: 0 16px 40px rgba(15,23,42,0.1); }

        .sv-card-glow {
          position: absolute; inset: -40px; border-radius: 50%;
          filter: blur(60px); pointer-events: none; z-index: 0;
        }

        .sv-card-body {
          position: relative; z-index: 1;
          padding: 32px 28px 20px;
          display: flex; flex-direction: column; gap: 0;
        }

        .sv-card-top {
          display: flex; align-items: center;
          justify-content: space-between; margin-bottom: 24px;
        }
        .sv-num {
          font-size: 3rem; font-weight: 900; line-height: 1;
          opacity: 0.22; letter-spacing: -0.04em;
          font-variant-numeric: tabular-nums;
        }
        .sv-icon-wrap {
          width: 56px; height: 56px; border-radius: 16px;
          display: flex; align-items: center; justify-content: center;
          backdrop-filter: blur(6px);
        }

        .sv-card-title {
          font-size: 1.45rem; font-weight: 900;
          color: var(--text); margin: 0 0 12px; line-height: 1.3;
        }
        .sv-card-desc {
          font-size: 0.96rem; color: black;
          line-height: 1.78; margin: 0 0 22px;
        }

        .sv-features {
          list-style: none; margin: 0 0 28px; padding: 0;
          display: flex; flex-direction: column; gap: 11px; flex: 1;
        }
        .sv-feature-item {
          display: flex; align-items: center; gap: 10px;
          font-size: 0.9rem; font-weight: 600; color: #475569;
        }
        .sv-check { font-size: 0.85rem; flex-shrink: 0; }

        .sv-card-btn {
          display: inline-flex; align-items: center; gap: 10px;
          background: transparent; border: 1px solid;
          border-radius: 10px; padding: 11px 18px;
          font-family: inherit; font-weight: 700; font-size: 0.92rem;
          cursor: pointer;
          transition: background 0.2s;
          width: fit-content;
        }
        .sv-card-btn:hover { background: rgba(15,23,42,0.04); }

        /* bottom accent bar */
        .sv-card-bar {
          height: 3px; width: 100%;
          opacity: 0;
          transition: opacity 0.3s;
        }
        .sv-card:hover .sv-card-bar { opacity: 1; }

        /* ── CTA ── */
        .sv-cta {
          position: relative;
          border-radius: 28px;
          background: #fff;
          border: 1px solid rgba(232,146,10,0.18);
          box-shadow: 0 8px 32px rgba(15,23,42,0.08);
          padding: 36px 40px;
          display: flex; align-items: center;
          gap: 28px; overflow: hidden;
        }
        .sv-cta-shimmer {
          position: absolute; inset: 0;
          background: linear-gradient(
            105deg,
            transparent 30%,
            rgba(232,146,10,0.04) 50%,
            transparent 70%
          );
          background-size: 200% 100%;
          animation: sv-shimmer 4s linear infinite;
          pointer-events: none;
        }
        @keyframes sv-shimmer {
          0%{ background-position: 200% 0; }
          100%{ background-position: -200% 0; }
        }

        .sv-cta-left {
          display: flex; align-items: center; gap: 22px; flex: 1; position: relative; z-index: 1;
        }
        .sv-cta-icon {
          width: 60px; height: 60px; flex-shrink: 0; border-radius: 18px;
          background: rgba(232,146,10,0.1); border: 1px solid rgba(232,146,10,0.25);
          display: flex; align-items: center; justify-content: center;
          color: var(--gold); font-size: 1.55rem;
        }
        .sv-cta-title {
          font-size: 1.5rem; font-weight: 900;
          color: var(--text); margin: 0 0 8px; line-height: 1.2;
        }
        .sv-cta-desc {
          font-size: 0.98rem; color: var(--muted);
          line-height: 1.7; margin: 0;
          color:#64748b;
        }
        .sv-cta-btn {
          position: relative; z-index: 1;
          display: inline-flex; align-items: center; gap: 10px;
          background: #6366f1;
          border: none; border-radius: 14px;
          padding: 15px 26px;
          font-family: inherit; font-weight: 800; font-size: 0.96rem;
          cursor: pointer; white-space: nowrap;
          box-shadow: 0 8px 28px rgba(99, 102, 241, 0.3);
          transition: transform 0.2s, box-shadow 0.2s;
        }
        .sv-cta-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 14px 36px rgba(99, 102, 241,  0.4);
        }

        @media(max-width:860px){
          .sv-cta { flex-direction: column; align-items: stretch; padding: 28px 24px; text-align: center; }
          .sv-cta-left { flex-direction: column; align-items: center; text-align: center; }
          .sv-cta-btn { justify-content: center; }
        }
        @media(max-width:768px){
          .sv-root { padding: 90px 0 64px; }
          .sv-container { padding: 0 20px; }
          .sv-header { margin-bottom: 48px; }
          .sv-card-body { padding: 26px 22px 16px; }
        }
      `}</style>
    </>
  );
};

export default Services;